import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector } from 'react-redux';
import {
    Box,
    Typography,
    Divider,
    Alert,
    Grid,
    Snackbar,
    Button,
    Modal,
    Paper,
    CircularProgress,
    Switch,
    Tooltip,
    IconButton
} from "@mui/material";
import ContentPackForm from "./ContentPackForm";
import {
    useCreateContentPackMutation,
    useUpdateContentPackMutation,
    useGetContentPackByIdQuery
} from '../../store/services/university-service';
import KeyboardArrowLeftTwoToneIcon from "@mui/icons-material/KeyboardArrowLeftTwoTone";
import ConfirmationDialog from "../../utils/confirmationDialog";
import TenancySelectionComponent from "../../components/TenancySelectionComponent";
import MainCard from "../../components/cards/MainCard";
import { useTheme } from "@mui/material/styles";
import { getCardColor } from "../../utils/colorUtils";
import HelpIcon from "@mui/icons-material/HelpOutline";
import SettingsIcon from '@mui/icons-material/Settings';
import TrimmedTextField from '../../components/TrimmedTextField';

export default function ContentPackDetails() {
    const { id } = useParams();
    const location = useLocation();
    const isPreset = new URLSearchParams(location.search).get('preset') === 'true';
    const isEditMode = Boolean(id);
    const navigate = useNavigate();
    const theme = useTheme();
    const lightMode = theme.palette.mode === "light";
    const [isAdmin, setIsAdmin] = useState(false);

    const [isSaving, setIsSaving] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogAction, setDialogAction] = useState(null);
    const [nameModalOpen, setNameModalOpen] = useState(!isEditMode);
    const [contentPackName, setContentPackName] = useState('');
    const [nameError, setNameError] = useState('');
    const [selectedCourses, setSelectedCourses] = useState([]);
    const [sharedTenancyIds, setSharedTenancyIds] = useState([]);
    const [publishToFamily, setPublishToFamily] = useState(false);
    const [published, setPublished] = useState(false);

    const {
        data: contentPackData,
        isLoading: isLoadingContentPack
    } = useGetContentPackByIdQuery(id, {
        skip: !isEditMode
    });

    const [createContentPack] = useCreateContentPackMutation();
    const [updateContentPack] = useUpdateContentPackMutation();

    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    const { tenancyId } = useSelector((state) => state.tenancy);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user'));
        setIsAdmin(user && user.role === "Administrators");
    }, []);

    useEffect(() => {
        if (isEditMode && contentPackData) {
            setContentPackName(contentPackData.contentPackName);
            setSelectedCourses(contentPackData.courseIds || []);
            setSharedTenancyIds(contentPackData.sharedTenancyIds || []);
            setPublishToFamily(contentPackData.publishToFamily || false);
            setPublished(contentPackData.published || false);
        } else if (!isAdmin && !isEditMode) {
            setSharedTenancyIds([tenancyId]);
            setPublished(true);
        }
    }, [isEditMode, contentPackData, isAdmin, tenancyId]);

    const initialData = React.useMemo(() => {
        if (isEditMode) {
            if (isLoadingContentPack) {
                return null;
            }
            return {
                contentPackName: contentPackData?.name || '',
                courseIds: contentPackData?.courseIds || [],
                tenancyId: contentPackData?.tenancyId || (isPreset ? 0 : tenancyId),
                published: contentPackData?.published || false,
                sharedTenancyIds: contentPackData?.sharedTenancyIds || [],
                publishToFamily: contentPackData?.publishToFamily || false
            };
        }
        return {
            contentPackName: contentPackName,
            courseIds: selectedCourses,
            tenancyId: isPreset ? 0 : tenancyId,
            published: published,
            sharedTenancyIds: sharedTenancyIds,
            publishToFamily: publishToFamily
        };
    }, [isEditMode, contentPackData, contentPackName, selectedCourses, isPreset,
        tenancyId, isLoadingContentPack, sharedTenancyIds, publishToFamily, published]);

    if (isEditMode && isLoadingContentPack) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    const handleNavigateBack = () => {
        if (isSaving) {
            setDialogOpen(true);
            setDialogAction(() => () => navigate(-1));
            return;
        }
        navigate(-1);
    };

    const handleNameSubmit = () => {
        if (!contentPackName.trim()) {
            setNameError('Content Pack Name is required');
            return;
        }
        setNameModalOpen(false);
    };

    const handleEditDetails = () => {
        setNameModalOpen(true);
    };

    const handleFormSave = (data) => {
        setSelectedCourses(data.content);
    };

    const handleSharedTenanciesChange = (tenancyIds) => {
        setSharedTenancyIds(tenancyIds);
    };

    const handlePublishToFamilyChange = (event) => {
        setPublishToFamily(event.target.checked);
    };

    const handlePublishedChange = (event) => {
        setPublished(event.target.checked);
    };

    const handleSaveContentPack = async () => {
        if (!contentPackName.trim() && !isEditMode) {
            setSnackbarSeverity("error");
            setSnackbarMessage("Content Pack Name is required");
            setSnackbarOpen(true);
            return;
        }

        if (selectedCourses.length === 0) {
            setSnackbarSeverity("error");
            setSnackbarMessage("Please select at least one course");
            setSnackbarOpen(true);
            return;
        }

        setIsSaving(true);
        try {
            const transformedData = isEditMode ? {
                name: contentPackName,
                tenancyId: contentPackData.tenancyId,
                published: isAdmin ? published : (contentPackData.published || true),
                courseIds: selectedCourses,
                sharedTenancyIds: isAdmin ? sharedTenancyIds : (contentPackData.sharedTenancyIds || [tenancyId]),
                publishToFamily: publishToFamily
            } : {
                name: contentPackName,
                tenancyId: tenancyId, // We no longer need to handle preset (tenancyId: 0) case separately
                published: isAdmin ? published : true, // Non-admins always create published content packs
                courseIds: selectedCourses,
                sharedTenancyIds: isAdmin ? sharedTenancyIds : [tenancyId], // Non-admins can only share with their tenancy
                publishToFamily: publishToFamily
            };

            const result = isEditMode
                ? await updateContentPack({
                    id: id,
                    contentPackData: transformedData
                }).unwrap()
                : await createContentPack(transformedData).unwrap();

            setSnackbarSeverity("success");
            setSnackbarMessage(`Content pack ${isEditMode ? 'updated' : 'created'} successfully!`);
            setSnackbarOpen(true);
            setIsSaving(false);

            await new Promise(resolve => setTimeout(resolve, 500));
            navigate('/packs');

        } catch (error) {
            console.error('Error saving content pack:', error);
            const errorMessage = error.data?.message ||
                `Failed to ${isEditMode ? 'update' : 'create'} content pack`;

            setSnackbarSeverity("error");
            setSnackbarMessage(errorMessage);
            setSnackbarOpen(true);
            setIsSaving(false);
        }
    };

    return (
        <Box sx={{
            width: '100%',
            height: '100%',
            overflow: 'auto'
        }}>
            <Grid container rowSpacing={2.5} columnSpacing={2.75}>
                <Grid item xs={12}>
                    <Grid container justifyContent="space-between" alignItems="center">
                        <Button
                            onClick={handleNavigateBack}
                            variant="contained"
                            startIcon={<KeyboardArrowLeftTwoToneIcon />}
                        >
                            Back
                        </Button>
                        {!nameModalOpen && (
                            <Button
                                variant="contained"
                                color="warning"
                                onClick={handleSaveContentPack}
                                disabled={(!contentPackName && !isEditMode) || selectedCourses.length === 0 || isSaving}
                            >
                                {isSaving ? (
                                    <>
                                        <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                                        {isEditMode ? 'Updating...' : 'Creating...'}
                                    </>
                                ) : (
                                    isEditMode ? 'Update Content Pack' : 'Create Content Pack'
                                )}
                            </Button>
                        )}
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Grid container justifyContent="space-between" sx={{ mb: 4 }}>
                        <Box display="flex" alignItems="center" width="100%">
                            <Typography variant="h2" mb={2}>
                                {isEditMode
                                    ? contentPackData
                                        ? `${contentPackData.contentPackName}`
                                        : "Loading..."
                                    : `${contentPackName ? `${contentPackName}` : ''}`}
                            </Typography>
                            {!nameModalOpen && (
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={handleEditDetails}
                                    endIcon={<SettingsIcon/>}
                                    sx={{ ml: 3, mb: 2, height: 40 }}
                                >
                                    Edit Details
                                </Button>
                            )}
                        </Box>
                        <Divider sx={{ width: "100%" }} />
                    </Grid>
                    <ContentPackForm
                        initialData={initialData}
                        onSave={handleFormSave}
                        mode={isEditMode ? "edit" : "add"}
                        isSaving={isSaving}
                    />
                </Grid>
            </Grid>

            <Modal
                open={nameModalOpen}
                disableEscapeKeyDown
                disableBackdropClick
                aria-labelledby="content-pack-name-modal"
            >
                <Paper sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    borderRadius: '8px',
                    width: 600,
                    p: 4,
                    outline: 'none',
                    backgroundColor: 'background.default',
                    maxHeight: '90vh',
                    overflow: 'auto'
                }}>
                    <Typography variant="h6" component="h2" gutterBottom>
                        Content Pack Details
                    </Typography>

                    {/* Content Pack Name */}
                    <TrimmedTextField
                        fullWidth
                        label="Content Pack Name"
                        value={contentPackName}
                        onChange={(e) => {
                            setContentPackName(e.target.value);
                            if (e.target.value.trim()) setNameError('');
                        }}
                        error={!!nameError}
                        helperText={nameError}
                        sx={{ mb: 3 }}
                    />

                    {/* Visibility Card - only for admins */}
                    {isAdmin && (
                        <MainCard
                            content={true}
                            boxShadow={true}
                            border={false}
                            sx={{
                                mb: 3,
                                bgcolor: getCardColor(lightMode, published)
                            }}
                        >
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <Typography variant="h4">Content Pack Visibility</Typography>
                                    <Tooltip title="When enabled, the content pack will be visible to users according to sharing settings" placement="top">
                                        <IconButton size="small">
                                            <HelpIcon sx={{ color: "secondary.main" }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Switch
                                    checked={published}
                                    onChange={handlePublishedChange}
                                    inputProps={{ "aria-label": "content pack visibility" }}
                                />
                            </Box>
                            <Typography variant="caption">
                                {published
                                    ? "The content pack is visible to users according to sharing settings"
                                    : "The content pack is currently hidden from all users"}
                            </Typography>
                        </MainCard>
                    )}

                    {/* Content Pack Sharing Settings Card */}
                    {isAdmin && (<MainCard
                        content={true}
                        boxShadow={true}
                        border={false}
                        sx={{ mb: 3, bgcolor: getCardColor(lightMode) }}
                    >
                        <Typography variant="h4" sx={{ mb: 2 }}>Content Pack Sharing Settings</Typography>

                        {/* Tenancy Selection Component - only editable for admins */}
                        {isAdmin ? (
                            <TenancySelectionComponent
                                selectedTenancies={sharedTenancyIds || []}
                                onChange={handleSharedTenanciesChange}
                            />
                        ) : (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    This content pack will be shared with your tenancy only.
                                </Typography>
                                <TrimmedTextField
                                    fullWidth
                                    label="Shared With"
                                    value="Your Tenancy"
                                    disabled
                                    size="small"
                                />
                            </Box>
                        )}
                    </MainCard>)}

                    <MainCard
                        content={true}
                        boxShadow={true}
                        border={false}
                        sx={{
                            mb: 3,
                            bgcolor: getCardColor(lightMode)
                        }}
                    >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Typography variant="body1">Share with Tenancy Family</Typography>
                                <Tooltip title="When enabled, this content pack will be accessible to all descendants of the selected tenancies" placement="top">
                                    <IconButton size="small">
                                        <HelpIcon sx={{ color: "secondary.main" }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Switch
                                checked={publishToFamily || false}
                                onChange={handlePublishToFamilyChange}
                                inputProps={{ "aria-label": "publish to family" }}
                                disabled={sharedTenancyIds?.includes(0)}
                            />
                        </Box>
                    </MainCard>

                    <Button
                        variant="contained"
                        onClick={handleNameSubmit}
                        disabled={!contentPackName.trim()}
                    >
                        {isEditMode ? 'Confirm' : 'Continue'}
                    </Button>
                </Paper>
            </Modal>

            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            <ConfirmationDialog
                open={dialogOpen}
                title="Unsaved Changes"
                content="You have unsaved changes. Are you sure you want to leave?"
                onConfirm={() => {
                    setDialogOpen(false);
                    dialogAction();
                }}
                onCancel={() => setDialogOpen(false)}
            />
        </Box>
    );
}